import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useFeatureFlags } from '@shared/context/useFeatureFlags';
import { usePermissions } from '@shared/context/usePermissions';
import {
  getClinicLocationClientAccountsByClinic,
  getLocationAccountNotes,
} from '@features/coverage/services/clientClinicApi';
import { ServiceUseTypeLabels } from '@features/coverage/types/clientClinic';
import type { ClinicDetailDto } from '../types/clinic';
import './ClinicInfoSection.css';
import './ClinicNotesSection.css';
import { withXmId } from '@shared/utils/xmId';

interface ClinicRelationshipNotesSectionProps {
  clinic: ClinicDetailDto;
}

function formatDate(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

/**
 * Read-only quick-reference of notes entered on this clinic's client
 * relationships. These are the same per-relationship note-history entries added
 * on the relationship detail screen (createLocationAccountNote); here they are
 * aggregated across all of the clinic's relationships, read-only.
 */
export default function ClinicRelationshipNotesSection({
  clinic,
}: Readonly<ClinicRelationshipNotesSectionProps>) {
  const flags = useFeatureFlags();
  const { hasAnyPermission } = usePermissions();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const canView =
    flags.clinicClientAccounts &&
    hasAnyPermission('UserCanListClinicRelationships', 'UserCanViewClinicRelationship');

  // Fetch the clinic's relationships, then each relationship's note history,
  // and flatten into one read-only list (newest first).
  const { data: notes = [], isLoading, isError } = useQuery({
    queryKey: ['clinicRelationshipNoteHistory', clinic.id],
    queryFn: async () => {
      const accounts = await getClinicLocationClientAccountsByClinic(clinic.id);
      // Fetch each relationship's note history independently. allSettled (not all)
      // so one relationship's failing request still lets the rest render rather
      // than blanking the whole list. (N+1 for now — a batched endpoint would be
      // the longer-term fix if relationship counts grow.)
      const settled = await Promise.allSettled(
        accounts.map(async (acct) => {
          const history = await getLocationAccountNotes(acct.id);
          return history.map((n) => ({
            id: n.id,
            noteText: n.noteText,
            noteType: n.noteType,
            createdAt: n.createdAt,
            customerName: acct.customerName,
            customerXmId: acct.customerXmId,
            locationName: acct.customerLocationName,
            serviceTypeKey: acct.serviceTypeKey,
            accountStatus: acct.accountStatus,
          }));
        })
      );
      return settled
        .flatMap((r) => (r.status === 'fulfilled' ? r.value : []))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    },
    enabled: canView && clinic.id > 0,
  });

  if (!canView) return null;

  return (
    <div className="info-section">
      <div className="info-section-header">
        <button
          type="button"
          className="info-section-toggle"
          onClick={() => setIsCollapsed(c => !c)}
          aria-expanded={!isCollapsed}
        >
          <ChevronDown size={16} className={`info-section-chevron ${isCollapsed ? 'collapsed' : ''}`} />
          <h3 className="info-section-title">Relationship Notes ({notes.length})</h3>
        </button>
      </div>

      {!isCollapsed && (
        <div className="clinic-notes-body">
          <div className="clinic-notes-hint">
            Read-only. Add or edit these on the relationship detail screen.
          </div>
          <div className="clinic-notes-list">
            {isLoading ? (
              <div className="clinic-notes-empty">Loading relationship notes...</div>
            ) : isError ? (
              <div className="clinic-notes-empty clinic-notes-error">
                Couldn&apos;t load relationship notes. Please refresh to try again.
              </div>
            ) : notes.length === 0 ? (
              <div className="clinic-notes-empty">No relationship notes</div>
            ) : (
              notes.map(note => (
                <div key={note.id} className="clinic-note-item">
                  <div className="clinic-note-item-header">
                    <span className="clinic-note-author">
                      {note.customerName ? withXmId(note.customerName, note.customerXmId) : 'Unknown customer'}
                      {note.locationName ? ` — ${note.locationName}` : ''}
                    </span>
                    <span className="clinic-note-date">
                      {ServiceUseTypeLabels[note.serviceTypeKey]} &middot; {note.accountStatus}
                      {note.noteType ? ` · ${note.noteType}` : ''} &middot; {formatDate(note.createdAt)}
                    </span>
                  </div>
                  <div className="clinic-note-text">{note.noteText}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
